import type { AdminRole } from '../adminApi';
import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';

export interface AdminMenuActions {
  /** The owner sees everything; venue staff (the access the owner generates) see the Leaderboard and today's activation code. */
  role: AdminRole;
  openBranding(): void;
  openUsageLog(): void;
  openAccessControl(): void;
  openVenues(): void;
  /** The leaderboard screens — the same for the owner and for venue staff. */
  openLeaderboard(): void;
  /** How many tables are waiting for approval — shown on the button so staff notice without opening it. */
  loadWaitingCount(): Promise<number>;
  /** Whether Leaderboard mode is switched on for players. */
  loadLeaderboardEnabled(): Promise<boolean>;
  /** Switches it on or off; resolves with the setting as saved, rejects with a message that can be shown. */
  setLeaderboardEnabled(enabled: boolean): Promise<boolean>;
  /** Whether every table must wait for staff approval. */
  loadApprovalRequired(): Promise<boolean>;
  setApprovalRequired(required: boolean): Promise<boolean>;
  /** Whether a venue admin secret exists. */
  loadVenueAdminConfigured(): Promise<boolean>;
  /** Makes a new venue admin secret (replacing any earlier one); resolves with it — shown once. */
  createVenueAdminSecret(): Promise<string>;
  removeVenueAdmin(): Promise<void>;
  exit(): void;
}

/** What each role can open from the menu (pure, so it is tested: venue staff must never be offered an owner screen). */
export type AdminMenuItem = 'branding' | 'usageLog' | 'accessControl' | 'venues' | 'leaderboard';

export function adminMenuItems(role: AdminRole): AdminMenuItem[] {
  // Venue staff (Indra, 2026-10-09): the whole Leaderboard, and Access Control reduced to today's activation code (its own screen).
  return role === 'owner' ? ['branding', 'usageLog', 'accessControl', 'venues', 'leaderboard'] : ['leaderboard', 'accessControl'];
}

const ITEM_LABEL: Record<AdminMenuItem, string> = {
  branding: 'Game Centre Branding',
  usageLog: 'Recognition Usage',
  accessControl: 'Access Control',
  venues: 'Venues',
  leaderboard: 'Leaderboard',
};

/** The button text, with the number of people waiting when there are any. */
export function menuItemLabel(item: AdminMenuItem, waiting: number): string {
  const showsWaiting = item === 'leaderboard';
  return showsWaiting && waiting > 0 ? `${ITEM_LABEL[item]} — ${waiting} waiting` : ITEM_LABEL[item];
}

export function renderAdminMenu(root: HTMLElement, actions: AdminMenuActions): void {
  const owner = actions.role === 'owner';
  const items = adminMenuItems(actions.role);
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Admin</h1>
      <p class="muted">
        ${
          owner
            ? 'Hidden from the normal player-facing flow — only reachable via this PIN-protected URL.'
            : "You are signed in as venue staff: you can run the leaderboard and give out today's activation code."
        }
      </p>

      <div class="stack">
        ${items
          .map(
            (item) => `
        <button type="button" id="menu-${item}" class="secondary" style="width:100%;text-align:left">${esc(ITEM_LABEL[item])}</button>`,
          )
          .join('')}
      </div>

      ${
        owner
          ? `
      ${switchHtml(
        'lbMode',
        'Leaderboard mode',
        "When it is off, the Leaderboard option disappears from every player's Mode Select and no new leaderboard games can start. Games already started can still send their result. Players' devices catch up the next time they open Mode Select.",
      )}
      ${switchHtml(
        'lbApproval',
        'Staff approve tables',
        "When it is on, a table waits for staff to approve it (under Leaderboard → Tables waiting for approval) before its game starts. When it is off, a table starts as soon as the host presses Start.",
      )}
      <div class="lb-switch" style="margin-top:0.75rem">
        <div class="lb-switch-row">
          <div>
            <strong>Venue admin access</strong>
            <div class="muted" id="vaStatus" role="status">Checking…</div>
          </div>
          <button type="button" id="vaCreate" class="secondary" disabled>…</button>
        </div>
        <div id="vaBox"></div>
        <p class="muted" style="margin:0.4rem 0 0">
          A separate code for floor staff, entered at their own address. It opens the Leaderboard screens (the same as yours) and
          today's activation code with Regenerate — not the switches here, the sessions list, venues, branding or recognition
          usage. Creating a new code stops the old one working.
        </p>
      </div>`
          : ''
      }

      <button type="button" id="exitBtn" class="ghost" style="width:100%;margin-top:1rem">
        Exit Admin
      </button>
    </div>
  `;

  const handlers: Record<AdminMenuItem, () => void> = {
    branding: () => actions.openBranding(),
    usageLog: () => actions.openUsageLog(),
    accessControl: () => actions.openAccessControl(),
    venues: () => actions.openVenues(),
    leaderboard: () => actions.openLeaderboard(),
  };
  for (const item of items) {
    root.querySelector<HTMLButtonElement>(`#menu-${item}`)!.addEventListener('click', handlers[item]);
  }
  root.querySelector<HTMLButtonElement>('#exitBtn')!.addEventListener('click', () => actions.exit());

  // The waiting count arrives a moment later, and a failure just leaves the plain label.
  const withCount = items.find((i) => i === 'leaderboard');
  if (withCount) {
    actions
      .loadWaitingCount()
      .then((n) => {
        root.querySelector<HTMLButtonElement>(`#menu-${withCount}`)!.textContent = menuItemLabel(withCount, n);
      })
      .catch(() => undefined);
  }

  if (owner) {
    wireSwitch(root, 'lbMode', actions.loadLeaderboardEnabled, actions.setLeaderboardEnabled, leaderboardSwitchText);
    wireSwitch(root, 'lbApproval', actions.loadApprovalRequired, actions.setApprovalRequired, tableApprovalSwitchText);
    wireVenueAdmin(root, actions);
  }
}

function switchHtml(id: string, title: string, help: string): string {
  return `
      <div class="lb-switch" style="margin-top:1rem">
        <div class="lb-switch-row">
          <div>
            <strong>${esc(title)}</strong>
            <div class="muted" id="${id}Status" role="status">Checking…</div>
          </div>
          <button type="button" id="${id}Toggle" class="secondary" disabled>…</button>
        </div>
        <p class="muted" style="margin:0.4rem 0 0">${esc(help)}</p>
      </div>`;
}

/** What the Leaderboard mode switch says, given the current setting (exported so the wording is tested). */
export function leaderboardSwitchText(enabled: boolean): { status: string; button: string } {
  return enabled
    ? { status: 'On — players can see and start leaderboard games.', button: 'Turn off' }
    : { status: 'Off — hidden from players.', button: 'Turn on' };
}

/** What the table approval switch says. */
export function tableApprovalSwitchText(required: boolean): { status: string; button: string } {
  return required
    ? { status: 'On — every table waits for staff approval before it plays.', button: 'Turn off' }
    : { status: 'Off — a table starts as soon as the host presses Start.', button: 'Turn on' };
}

/** What the RETIRED per-player approval switch said (kept only so its wording stays tested; the menu no longer shows it). */
export function approvalSwitchText(required: boolean): { status: string; button: string } {
  return required
    ? { status: 'On — new players wait for staff approval.', button: 'Turn off' }
    : { status: 'Off — anyone can join and play at once.', button: 'Turn on' };
}

/** A load-then-toggle row: shows the current value, and each tap saves the opposite. */
function wireSwitch(
  root: HTMLElement,
  id: string,
  load: () => Promise<boolean>,
  save: (value: boolean) => Promise<boolean>,
  textFor: (value: boolean) => { status: string; button: string },
): void {
  const status = root.querySelector<HTMLElement>(`#${id}Status`)!;
  const toggle = root.querySelector<HTMLButtonElement>(`#${id}Toggle`)!;
  let current: boolean | undefined;

  const show = (value: boolean) => {
    current = value;
    const text = textFor(value);
    status.textContent = text.status;
    toggle.textContent = text.button;
    toggle.disabled = false;
  };

  load()
    .then(show)
    .catch(() => {
      status.textContent = "Couldn't check the setting. Check your connection and reopen this screen.";
    });

  toggle.addEventListener('click', () => {
    if (current === undefined) return;
    toggle.disabled = true;
    status.textContent = 'Saving…';
    save(!current)
      .then(show)
      .catch((err: unknown) => {
        status.textContent = err instanceof Error ? err.message : "The change wasn't saved.";
        toggle.disabled = false;
      });
  });
}

/**
 * The venue admin access row: shows whether a code exists, creates or replaces it (asking first if that would stop
 * a working one), shows a new code ONCE, and removes it. The code is never readable again after that screen.
 */
function wireVenueAdmin(root: HTMLElement, actions: AdminMenuActions): void {
  const status = root.querySelector<HTMLElement>('#vaStatus')!;
  const create = root.querySelector<HTMLButtonElement>('#vaCreate')!;
  const box = root.querySelector<HTMLElement>('#vaBox')!;
  let configured: boolean | undefined;

  const show = (value: boolean) => {
    configured = value;
    status.textContent = value ? 'Active — floor staff can sign in with the code.' : 'Not set up.';
    create.textContent = value ? 'New code' : 'Create code';
    create.disabled = false;
    if (!value) box.innerHTML = '';
  };

  const askFirst = (question: string, yesLabel: string, onYes: () => void) => {
    box.innerHTML = `
      <div class="error-box" style="margin-top:0.5rem">${esc(question)}</div>
      <div class="row" style="margin-top:0.5rem">
        <button type="button" id="vaYes" class="danger">${esc(yesLabel)}</button>
        <button type="button" id="vaNo" class="ghost">Cancel</button>
      </div>`;
    box.querySelector<HTMLButtonElement>('#vaYes')!.addEventListener('click', onYes);
    box.querySelector<HTMLButtonElement>('#vaNo')!.addEventListener('click', () => (box.innerHTML = configured ? removeLink() : ''));
  };
  const removeLink = () => '<button type="button" id="vaRemove" class="ghost" style="width:100%;margin-top:0.5rem">Remove venue admin access…</button>';

  const generate = () => {
    create.disabled = true;
    status.textContent = 'Creating…';
    actions
      .createVenueAdminSecret()
      .then((secret) => {
        show(true);
        box.innerHTML = `
          <div class="lb-note" style="margin-top:0.5rem">
            <div class="muted">Venue admin code — shown only now</div>
            <div id="vaSecret" style="font-family:monospace;font-size:1.25rem;font-weight:700;letter-spacing:0.05em;word-break:break-all">${esc(secret)}</div>
          </div>
          <p class="muted" style="margin:0.4rem 0 0">Give it to floor staff in person or by a private message. Nobody, including you, can look it up again — if it is lost, create a new one. It is typed in when they open the admin page.</p>
          <div class="row" style="margin-top:0.5rem">
            <button type="button" id="vaCopy" class="secondary">Copy</button>
          </div>
          ${removeLink()}`;
        wireRemove();
        box.querySelector<HTMLButtonElement>('#vaCopy')!.addEventListener('click', () => {
          void navigator.clipboard?.writeText(secret).then(
            () => (box.querySelector('#vaCopy')!.textContent = 'Copied'),
            () => (box.querySelector('#vaCopy')!.textContent = 'Select and copy it by hand'),
          );
        });
      })
      .catch((err: unknown) => {
        status.textContent = err instanceof Error ? err.message : "The code wasn't created.";
        create.disabled = false;
      });
  };

  const wireRemove = () => {
    box.querySelector<HTMLButtonElement>('#vaRemove')?.addEventListener('click', () =>
      askFirst('Remove venue admin access? Floor staff will be signed out and can no longer approve players.', 'Yes, remove it', () => {
        actions
          .removeVenueAdmin()
          .then(() => show(false))
          .catch((err: unknown) => {
            status.textContent = err instanceof Error ? err.message : "It wasn't removed.";
          });
      }),
    );
  };

  actions
    .loadVenueAdminConfigured()
    .then((value) => {
      show(value);
      if (value) {
        box.innerHTML = removeLink();
        wireRemove();
      }
    })
    .catch(() => {
      status.textContent = "Couldn't check. Check your connection and reopen this screen.";
    });

  create.addEventListener('click', () => {
    if (configured === undefined) return;
    if (configured) {
      askFirst('Create a new code? The current one stops working straight away, and anyone using it is signed out.', 'Yes, create a new one', generate);
    } else {
      generate();
    }
  });
}

// ---------------------------------------------------------------------------
// The two screens before the menu: asking for the secret, and saying when it can't be checked
// ---------------------------------------------------------------------------

/** The wording of the secret prompt; the venue staff address uses its own (it only ever takes the venue admin code). */
export interface AdminSecretPromptCopy {
  title: string;
  intro: string;
  label: string;
}

const OWNER_SECRET_PROMPT: AdminSecretPromptCopy = {
  title: 'Admin Secret Required',
  intro: 'Separate from the branding PIN. Enter the admin secret. It is remembered on this device after that.',
  label: 'Admin secret',
};

export function renderAdminSecretPrompt(
  root: HTMLElement,
  error: string | undefined,
  actions: { submit(secret: string): void; cancel(): void },
  copy: AdminSecretPromptCopy = OWNER_SECRET_PROMPT,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${esc(copy.title)}</h1>
      <p class="muted">${esc(copy.intro)}</p>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      <label for="secretInput">${esc(copy.label)}</label>
      <input type="password" id="secretInput" autocomplete="off" />
      <div class="row" style="margin-top:1rem">
        <button type="button" id="cancelBtn" class="secondary">Back</button>
        <button type="button" id="continueBtn">Continue</button>
      </div>
    </div>
  `;
  const input = root.querySelector<HTMLInputElement>('#secretInput')!;
  const submit = () => actions.submit(input.value.trim());
  root.querySelector<HTMLButtonElement>('#cancelBtn')!.addEventListener('click', () => actions.cancel());
  root.querySelector<HTMLButtonElement>('#continueBtn')!.addEventListener('click', submit);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });
}

export function renderAdminMessage(
  root: HTMLElement,
  title: string,
  message: string,
  actions: { retry?(): void; exit(): void },
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${esc(title)}</h1>
      <p class="muted">${esc(message)}</p>
      <div class="row" style="margin-top:1rem">
        <button type="button" id="exitBtn" class="secondary">Exit Admin</button>
        ${actions.retry ? '<button type="button" id="retryBtn">Try again</button>' : ''}
      </div>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#exitBtn')!.addEventListener('click', () => actions.exit());
  root.querySelector<HTMLButtonElement>('#retryBtn')?.addEventListener('click', () => actions.retry!());
}
